import React, { useEffect, useRef, useState, useMemo } from "react";
import { SearchOutlined } from '@ant-design/icons';
import type { InputRef, TableColumnsType, TableColumnType } from 'antd';
import { Button, Input, Modal, Space, Table } from 'antd';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import "./list_data_work_order.css";
import axios from "axios";
import useSWR from "swr";

const fetcher = (url: string) => axios.get(url).then((res) => res.data);

interface WarehouseType {
    warehouse_code: string;
    warehouse_name: string;
}

interface DataType {
    key: React.Key;
    work_order_no: string;
    bill_to_name: string;
    bill_to_code: string;
    work_order_date: string;
    service_advisor: string;
    warehouse: string;
    [key: string]: any;
}

type DataIndex = keyof DataType;
interface ListDataWorkOrderProps {
    onRowSelect: (record: DataType) => void;
}
const ListDataWorkOrder: React.FC<ListDataWorkOrderProps> = ({ onRowSelect }) => {

    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);
    const [isSelecting, setIsSelecting] = useState(false);
    const { data: dataSource, error, isLoading } = useSWR<DataType[]>(
        `${process.env.REACT_APP_BACKEND_URL}/api/work-order-invoice/list-data`,
        fetcher
    );

    const uniqueDataSource = useMemo(() => {
        if (!dataSource) return [];
        const seen = new Set();
        return dataSource.filter(item => {
            const duplicate = seen.has(item.work_order_no);
            seen.add(item.work_order_no);
            return !duplicate;
        });
    }, [dataSource]);

    const { data: existingInvoices } = useSWR<any[]>(
        `${process.env.REACT_APP_BACKEND_URL}/api/work-order-invoice/list-invoice`,
        fetcher
    );

    const { data: warehouseData } = useSWR<WarehouseType[]>(
        `${process.env.REACT_APP_BACKEND_URL}/api/warehouse/list-data`,
        fetcher
    );

    const [warehouseMap, setWarehouseMap] = useState<Map<string, string>>(new Map());

    useEffect(() => {
        if (warehouseData) {
            const newMap = new Map(warehouseData.map(item => [item.warehouse_code, item.warehouse_name]));
            setWarehouseMap(newMap);
        }
    }, [warehouseData]);

    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };

    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps['confirm'],
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(String(dataIndex));
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<DataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(String(dataIndex));
                        }}
                    >
                        Filter
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => close()}
                    >
                        Close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) =>
            record[dataIndex]
                ? record[dataIndex].toString().toLowerCase().includes((value as string).toLowerCase()) : false,
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });


    const columns: TableColumnsType<DataType> = [
        {
            title: 'Work Order No',
            dataIndex: 'work_order_no',
            key: 'work_order_no',
            ...getColumnSearchProps('work_order_no'),
            sorter: (a, b) => a.work_order_no.localeCompare(b.work_order_no),
        },
        {
            title: 'Customer Name',
            dataIndex: 'bill_to_name',
            key: 'bill_to_name',
            ...getColumnSearchProps('bill_to_name' as any),
            sorter: (a, b) => a.bill_to_name.localeCompare(b.bill_to_name),
        },
        {
            title: 'Customer Code',
            dataIndex: 'bill_to_code',
            key: 'bill_to_code',
            ...getColumnSearchProps('bill_to_code' as any),
            sorter: (a, b) => a.bill_to_code.localeCompare(b.bill_to_code),
        },
        {
            title: 'Service Date',
            dataIndex: 'work_order_date',
            key: 'work_order_date',
            ...getColumnSearchProps('work_order_date' as any),
            sorter: (a, b) => new Date(a.work_order_date).getTime() - new Date(b.work_order_date).getTime(),
        },
        {
            title: 'Advisor',
            dataIndex: 'service_advisor',
            key: 'service_advisor',
            ...getColumnSearchProps('service_advisor' as any),
            sorter: (a, b) => a.service_advisor.localeCompare(b.service_advisor),
        },
        {
            title: 'Warehouse',
            dataIndex: 'warehouse',
            key: 'warehouse',
            ...getColumnSearchProps('warehouse'),
            onFilter: (value, record) => {
                const warehouseName = warehouseMap.get(record.warehouse) || record.warehouse;
                return warehouseName.toString().toLowerCase().includes((value as string).toLowerCase());
            },
            render: (warehouseCode: string) => {
                const warehouseName = warehouseMap.get(warehouseCode) || warehouseCode;
                return searchedColumn === 'warehouse' ? (
                    <Highlighter
                        highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                        searchWords={[searchText]}
                        autoEscape
                        textToHighlight={warehouseName ? warehouseName.toString() : ''}
                    />
                ) : (
                    warehouseName
                );
            },
            sorter: (a, b) => {
                const nameA = warehouseMap.get(a.warehouse) || a.warehouse;
                const nameB = warehouseMap.get(b.warehouse) || b.warehouse;
                return nameA.localeCompare(nameB);
            },
        },
    ];

    return (
        <div id="icjj" style={transformStyle}>
            <div className="gjs-grid-row" id="iboo">
                <div className="gjs-grid-column" id="i4db">
                    <Table<DataType>
                        columns={columns}
                        dataSource={uniqueDataSource}
                        loading={isLoading || isSelecting}
                        rowKey="id"
                        pagination={{ pageSize: 10 }}
                        bordered
                        size="small"
                        onRow={(record) => ({
                            onClick: () => {
                                if (isSelecting) return;
                                const isExist = existingInvoices?.some(inv => inv.work_order_no === record.work_order_no);
                                if (isExist) {
                                    Modal.warning({ title: 'Peringatan', content: 'data sudah di input !' });
                                } else {
                                    setIsSelecting(true);
                                    onRowSelect(record);
                                }
                            },
                        })}
                    />
                </div>
            </div>
        </div>
    );
};

export default ListDataWorkOrder;